/**
 * The CSS custom properties the parent shell installs for the brand a family is viewing.
 *
 * `brandProfiles.ts` says what a brand *is* — its name, mark and accent. This file says how
 * the parent portal's chrome is drawn in that brand: the accent plus the handful of shape and
 * type decisions that differ between a bilingual after-school portal and an early-learning one.
 *
 * Why variables rather than props: the blue being replaced is spread across a dozen call sites
 * in six files (the recommended card, the week calendar, the catalog tabs, the program page),
 * and threading a profile through all of them means every future surface has to remember to do
 * the same. One set of tokens on the shell means a call site writes `var(--brand-accent)` and is
 * correct on both brands forever.
 *
 * **The defaults live in `index.css`, and they are BHCS's.** Anything rendered outside
 * `BrandProfileProvider` — the admin portal, the teacher portal, login, the printable invoice —
 * therefore keeps exactly the blue it has always had, without knowing brands exist.
 *
 * Not a security boundary, same as the rest of the brand layer: these decide what a parent is
 * *looking at*, never what they may read.
 */

import { BRAND_PROFILES } from './brandProfiles';
import type { BrandKey } from './brands';

/** The system font stack `index.css` sets on `body` — the portal's face before any brand. */
const SYSTEM_STACK =
    "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, " +
    "sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'";

/**
 * Nunito, already loaded by `index.html`, ahead of the same fallbacks. `ui-rounded` sits first
 * among them so a device that blocks the webfont still lands on something rounded (SF Rounded
 * on Apple platforms) rather than dropping straight back to the default face.
 */
const ROUNDED_STACK =
    "'Nunito', ui-rounded, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, " +
    "Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'";

/** How one brand draws the parent shell, beyond the colors its profile already carries. */
export interface BrandShellTheme {
    /**
     * Border tint for soft chips — the recommendation reason, and anything else that pairs a
     * tinted fill with a hairline edge. A separate token because `accent` itself is far too
     * strong at that size: the chip reads as a button rather than a label.
     */
    readonly accentEdge: string;
    /** The shell's font stack. */
    readonly font: string;
    /**
     * Weight for a program's name on its card.
     *
     * It is a token rather than a fixed `font-semibold` because weight is not comparable across
     * faces: Nunito at 600 reads lighter than the system face at 600, so the rounded brand needs
     * 800 to land where BHCS lands at 600.
     */
    readonly nameWeight: string;
    /**
     * Weight for the header wordmark, which is bold on BHCS where a program name is semibold,
     * and so cannot share `nameWeight` however tempting the single token looks.
     */
    readonly wordmarkWeight: string;
    /**
     * The recommended-program card's button. Its own token set because that button is the one
     * place where the two brands genuinely differ in shape: a 28px BHCS button sits in a dense
     * bilingual card, and Kaleido's is a pill with a real tap target.
     */
    readonly cta: {
        readonly radius: string;
        readonly padding: string;
        readonly fontSize: string;
        readonly fontWeight: string;
        readonly minHeight: string;
    };
    /** Radius for page-level primary buttons, which set their own padding and size. */
    readonly buttonRadius: string;
    /** Fill for the tinted panel on a program page ("About this program"). */
    readonly panel: string;
    /** Its hairline border — a shade lighter than `accentEdge`, which would fence in a block this big. */
    readonly panelEdge: string;
    /** Heading color that reads on `panel`. */
    readonly panelInk: string;
}

export const BRAND_SHELL_THEMES: Record<BrandKey, BrandShellTheme> = {
    // Every value below is the literal the BHCS markup used before these tokens existed:
    // blue-200 edges, `font-semibold` names, `rounded-lg px-3 py-1.5 text-xs font-medium`
    // buttons, `rounded-md` page buttons, a blue-50 → indigo-50 panel under a blue-900 heading.
    // BHCS is meant to render pixel for pixel as it did, so this row is a transcription, not a
    // redesign, and it doubles as the default set in `index.css`.
    bhcs: {
        accentEdge: '#bfdbfe',
        font: SYSTEM_STACK,
        nameWeight: '600',
        wordmarkWeight: '700',
        cta: {
            radius: '0.5rem',
            padding: '0.375rem 0.75rem',
            fontSize: '0.75rem',
            fontWeight: '500',
            minHeight: '0',
        },
        buttonRadius: '0.375rem',
        panel: 'linear-gradient(to right, #eff6ff, #eef2ff)',
        panelEdge: '#dbeafe',
        panelInk: '#1e3a8a',
    },
    kaleido: {
        accentEdge: '#e2d4ef',
        font: ROUNDED_STACK,
        nameWeight: '800',
        wordmarkWeight: '800',
        cta: {
            radius: '9999px',
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            fontWeight: '700',
            // 38px. The BHCS button is 28px tall, which is under every published minimum for a
            // touch target; Kaleido's families are on phones and this is its primary action.
            minHeight: '2.375rem',
        },
        buttonRadius: '9999px',
        panel: 'linear-gradient(to right, #f4eef9, #f8f4fb)',
        panelEdge: '#ece0f5',
        panelInk: '#4a2f6b',
    },
};

/**
 * The style object the parent shell spreads onto its outermost element.
 *
 * Returned as a plain record because React's `CSSProperties` has no room for custom properties;
 * the caller casts once at the point it is applied.
 */
export function brandCssVars(brand: BrandKey): Record<string, string> {
    const profile = BRAND_PROFILES[brand];
    const theme = BRAND_SHELL_THEMES[brand];
    return {
        '--brand-accent': profile.accent,
        '--brand-accent-ink': profile.accentInk,
        '--brand-accent-soft': profile.accentSoft,
        '--brand-accent-edge': theme.accentEdge,
        '--brand-font': theme.font,
        '--brand-name-weight': theme.nameWeight,
        '--brand-wordmark-weight': theme.wordmarkWeight,
        '--brand-cta-radius': theme.cta.radius,
        '--brand-cta-padding': theme.cta.padding,
        '--brand-cta-text': theme.cta.fontSize,
        '--brand-cta-weight': theme.cta.fontWeight,
        '--brand-cta-min-h': theme.cta.minHeight,
        '--brand-btn-radius': theme.buttonRadius,
        '--brand-panel': theme.panel,
        '--brand-panel-edge': theme.panelEdge,
        '--brand-panel-ink': theme.panelInk,
    };
}
